import { IconEdit, IconEye, IconTrash } from "@tabler/icons-react";
import { warehouseApi } from "apis";
import CustomBadge from "components/ui/CustomBadge/CustomBadge";
import CustomTable from "components/ui/CustomTable";
import CustomText from "components/ui/CustomText/CustomText";
import GroupActions from "components/ui/GroupActions";
import { ActionItem } from "components/ui/GroupActions/GroupActions";
import { STATUS } from "constants/common/common";
import { CommonParamsType } from "constants/types/common";
import { WarehouseResponseType } from "constants/types/warehouse";
import { MRT_ColumnDef, MRT_ColumnFiltersState } from "mantine-react-table";
import { FORM_IDS } from "pages/Warehouse/config";
import { FC, useMemo, useRef, useCallback } from "react";
import { useNotification, useWarehouses } from "utils/hooks";
import { useAppModal } from "utils/hooks/ui/useAppModal";
import { getWarehouseListKey } from "utils/hooks/request/useWarehouses";
import { Loading } from "components";
import { useSWRConfig } from "swr";
import EditForm from "../EditForm/EditForm";
import { useAbility } from "configs/casl";
import { canAccessRequirement } from "utils/helpers/authorization";

interface WarehouseTableProps {
  params: CommonParamsType;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
}

const ICON_SIZE = 18;

const WarehouseTable: FC<WarehouseTableProps> = ({
  params,
  onPageChange,
  onLimitChange,
}) => {
  const { notification } = useNotification();
  const { mutate } = useSWRConfig();
  const ability = useAbility();

  const {
    warehouses,
    pagination,
    isLoading,
    mutate: localMutate,
  } = useWarehouses(params);

  const { openCrudModal, closeAll, closeCrudModal } = useAppModal();
  const activeModalId = useRef<string | null>(null);

  const handleCloseModal = useCallback(() => {
    if (activeModalId.current) {
      closeCrudModal(activeModalId.current);
      activeModalId.current = null;
    }
  }, [closeCrudModal]);

  const handleDeleteWarehouse = useCallback(
    async (warehouseId: string) => {
      try {
        await warehouseApi.delete(warehouseId);
        notification.success({ title: "Xóa nhà kho thành công" });
        closeAll();
        localMutate();
        onPageChange(1);
      } catch {
        notification.error({ title: "Xóa nhà kho thất bại" });
      }
    },
    [notification, closeAll, localMutate, onPageChange],
  );

  const handleOpenDeleteModal = useCallback(
    (warehouseId: string) => {
      openCrudModal(
        <CustomText variant="section-title">Xác nhận xóa nhà kho</CustomText>,
        {
          mode: "delete",
          formId: FORM_IDS.DELETE,
          onConfirmDelete: async () => await handleDeleteWarehouse(warehouseId),
        },
      );
    },
    [openCrudModal, handleDeleteWarehouse],
  );

  const handleOpenEditModal = useCallback(
    (warehouse: WarehouseResponseType) => {
      const modalId = openCrudModal(
        <CustomText variant="section-title">Chỉnh sửa nhà kho</CustomText>,
        {
          mode: "edit",
          formId: FORM_IDS.EDIT,
          children: (
            <EditForm
              warehouse={warehouse}
              mutate={() => mutate(getWarehouseListKey(params))}
              onClose={handleCloseModal}
            />
          ),
          submitText: "Cập nhật",
        },
        500,
      );
      activeModalId.current = modalId;
    },
    [openCrudModal, mutate, params, handleCloseModal],
  );

  const warehouseActions: ActionItem<WarehouseResponseType>[] = useMemo(
    () => [
      {
        key: "view",
        label: "Xem chi tiết",
        icon: <IconEye size={ICON_SIZE} />,
        color: "blue",
        permission: "warehouse:read",
        onClick: (_row) => {
          // navigate(`/warehouse/${row.id}`)
        },
      },
      {
        key: "edit",
        label: "Chỉnh sửa",
        icon: <IconEdit size={ICON_SIZE} />,
        color: "orange",
        permission: "warehouse:update",
        onClick: (row) => handleOpenEditModal(row),
      },
      {
        key: "delete",
        label: "Xóa",
        icon: <IconTrash size={ICON_SIZE} />,
        color: "red",
        permission: "warehouse:delete",
        onClick: (row) => handleOpenDeleteModal(row.id),
      },
    ],
    [handleOpenEditModal, handleOpenDeleteModal],
  );

  const hasActions = useMemo(
    () => warehouseActions.some((a) => canAccessRequirement(ability, a.permission, true)),
    [warehouseActions, ability],
  );

  const columns: MRT_ColumnDef<WarehouseResponseType>[] = [
    {
      id: "stt",
      header: "STT",
      size: 5,
      grow: false,
      Cell: ({ row }) => row.index + 1,
      mantineTableHeadCellProps: { align: "center" },
      mantineTableBodyCellProps: { align: "center" },
    },
    {
      accessorKey: "code",
      header: "Mã kho",
    },
    {
      accessorKey: "name",
      header: "Tên kho",
    },
    {
      accessorKey: "type",
      header: "Loại kho",
    },
    {
      accessorKey: "description",
      header: "Mô tả",
      Cell: ({ cell }) => cell.getValue<string>() ?? "—",
    },
    {
      accessorKey: "isActive",
      header: "Trạng thái",
      grow: false,
      Cell: ({ cell }) =>
        cell.getValue() ? (
          <CustomBadge label={STATUS.ACTIVE_LABEL} color="green" />
        ) : (
          <CustomBadge label={STATUS.INACTIVE_LABEL} color="gray" />
        ),
    },
    ...(hasActions
      ? [
          {
            id: "actions",
            header: "Thao tác",
            enableSorting: false,
            enableColumnFilter: false,
            size: 50,
            grow: false,
            enableResizing: false,
            mantineTableHeadCellProps: { align: "center" },
            mantineTableBodyCellProps: { align: "center" },
            Cell: ({ row }: any) => (
              <GroupActions row={row.original} actions={warehouseActions} />
            ),
          } satisfies MRT_ColumnDef<WarehouseResponseType>,
        ]
      : []),
  ];

  const handleChange = useCallback(
    (state: {
      page: number;
      limit: number;
      filters?: MRT_ColumnFiltersState;
    }) => {
      if (pagination && state.page !== pagination.page) {
        onPageChange(state.page);
      } else if (pagination && state.limit !== pagination.limit) {
        onLimitChange(state.limit);
      }
    },
    [onPageChange, onLimitChange, pagination],
  );

  return (
    <Loading variant="overlay" visible={isLoading}>
      <CustomTable
        columns={columns}
        data={warehouses ?? []}
        pagination={pagination!}
        onStateChange={handleChange}
      />
    </Loading>
  );
};

export default WarehouseTable;
